import React, { useState } from 'react';
import { Bug, ChevronDown, ChevronRight, CheckCircle2, Box } from 'lucide-react';
import type { AlgorithmPhaseDebug, PalletLayout, PhaseSnapshot } from '@/types/loading-plan';
import { Pallet3DViewer } from './Pallet3DViewer';

interface AlgorithmDebugPanelProps {
  trace: AlgorithmPhaseDebug[];
  palletLengthCm: number;
  palletWidthCm: number;
  palletBaseHeightCm: number;
}

const PHASE_COLORS: Record<number, string> = {
  0: 'border-blue-300 bg-blue-50',
  1: 'border-indigo-300 bg-indigo-50',
  2: 'border-green-300 bg-green-50',
  3: 'border-amber-300 bg-amber-50',
  4: 'border-orange-300 bg-orange-50',
  5: 'border-slate-300 bg-slate-50',
  6: 'border-purple-300 bg-purple-50',
};

const PHASE_DOT: Record<number, string> = {
  0: 'bg-blue-500',
  1: 'bg-indigo-500',
  2: 'bg-green-500',
  3: 'bg-amber-500',
  4: 'bg-orange-500',
  5: 'bg-slate-400',
  6: 'bg-purple-500',
};

function DetailsRenderer({ data }: { data: any }) {
  if (data === null || data === undefined) return null;

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="text-slate-400 italic">vacio</span>;
    return (
      <div className="space-y-1">
        {data.map((item, i) => (
          <div key={i} className="bg-white rounded border border-slate-100 px-2.5 py-1.5 text-[11px]">
            <DetailsRenderer data={item} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data);
    return (
      <div className="space-y-0.5">
        {entries.map(([key, val]) => (
          <div key={key} className="flex gap-2 text-[11px]">
            <span className="text-slate-400 font-medium shrink-0 min-w-[80px]">{key}:</span>
            <span className="text-slate-700 font-mono break-all">
              {Array.isArray(val) && val.length > 0 && typeof val[0] !== 'object' ? (
                val.join(', ')
              ) : typeof val === 'object' && val !== null ? (
                <DetailsRenderer data={val} />
              ) : (
                String(val)
              )}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return <span className="text-slate-700 font-mono text-[11px]">{String(data)}</span>;
}

function SnapshotViewer({ snapshots, palletLengthCm, palletWidthCm, palletBaseHeightCm }: {
  snapshots: PhaseSnapshot[];
  palletLengthCm: number;
  palletWidthCm: number;
  palletBaseHeightCm: number;
}) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [show3D, setShow3D] = useState(false);
  const snap = snapshots[selectedIdx];
  if (!snap) return null;

  const layout: PalletLayout = {
    length: palletLengthCm,
    width: palletWidthCm,
    baseHeight: palletBaseHeightCm,
    items: snap.layout,
  };

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
      {/* Snapshot selector */}
      {snapshots.length > 1 && (
        <div className="flex gap-1 p-2 overflow-x-auto bg-white border-b border-slate-100">
          {snapshots.map((s, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setSelectedIdx(idx)}
              className={`text-[10px] py-1 px-2 rounded-md font-medium whitespace-nowrap transition-colors ${
                idx === selectedIdx
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Snapshot info */}
      <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-slate-100">
        <div className="flex gap-4 text-[11px] text-slate-600">
          <span><strong>{snap.totalBoxes}</strong> cajas</span>
          <span><strong>{snap.weightKg.toFixed(1)}</strong> kg</span>
          <span><strong>{snap.heightCm.toFixed(1)}</strong> cm</span>
        </div>
        <button
          type="button"
          onClick={() => setShow3D(true)}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
        >
          <Box className="h-3 w-3" />
          VER 3D
        </button>
      </div>

      {/* Inline 3D preview */}
      <div className="hidden lg:block" style={{ height: 350 }}>
        <Pallet3DViewer
          palletId={null}
          type="virtual"
          isOpen={true}
          onClose={() => {}}
          palletNumber={`P${snap.palletIndex + 1}`}
          embedded={true}
          initialData={layout}
        />
      </div>

      {/* Dialog 3D for mobile / full screen */}
      {show3D && (
        <Pallet3DViewer
          palletId={null}
          type="virtual"
          isOpen={show3D}
          onClose={() => setShow3D(false)}
          palletNumber={snap.label}
          initialData={layout}
        />
      )}
    </div>
  );
}

export function AlgorithmDebugPanel({ trace, palletLengthCm, palletWidthCm, palletBaseHeightCm }: AlgorithmDebugPanelProps) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [open, setOpen] = useState(false);

  const toggle = (phase: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(phase)) next.delete(phase);
      else next.add(phase);
      return next;
    });
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 overflow-hidden">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-amber-100/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Bug className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-semibold text-amber-900">Debug del algoritmo</span>
          <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-medium">
            {trace.length} pasadas
          </span>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-amber-500" /> : <ChevronRight className="h-4 w-4 text-amber-500" />}
      </button>

      {open && (
        <div className="border-t border-amber-200 bg-white">
          {/* Timeline */}
          <div className="relative">
            {trace.map((phase, idx) => {
              const isExpanded = expanded.has(phase.phase);
              const colorClass = PHASE_COLORS[phase.phase] ?? 'border-slate-300 bg-slate-50';
              const dotClass = PHASE_DOT[phase.phase] ?? 'bg-slate-400';
              const isLast = idx === trace.length - 1;
              const hasSnapshots = phase.snapshots && phase.snapshots.length > 0;

              return (
                <div key={phase.phase} className="relative">
                  {/* Timeline line */}
                  {!isLast && (
                    <div className="absolute left-5 top-0 bottom-0 w-px bg-slate-200" />
                  )}

                  {/* Phase header */}
                  <button
                    type="button"
                    onClick={() => toggle(phase.phase)}
                    className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
                  >
                    {/* Dot */}
                    <div className="relative z-10 flex items-center justify-center w-4 h-4 mt-0.5 shrink-0">
                      <div className={`w-3 h-3 rounded-full ${dotClass} ring-2 ring-white`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-800">
                          Pasada {phase.phase}
                        </span>
                        <span className="text-[10px] text-slate-500 font-medium">
                          {phase.name}
                        </span>
                        {hasSnapshots && (
                          <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                            3D
                          </span>
                        )}
                        {isExpanded
                          ? <ChevronDown className="h-3 w-3 text-slate-400 ml-auto shrink-0" />
                          : <ChevronRight className="h-3 w-3 text-slate-400 ml-auto shrink-0" />}
                      </div>

                      {/* Summary - always visible */}
                      <div className={`mt-1 text-[11px] font-mono px-2 py-1 rounded border ${colorClass}`}>
                        <CheckCircle2 className="h-3 w-3 inline mr-1 text-green-500" />
                        {phase.summary}
                      </div>
                    </div>
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="ml-10 mr-3 mb-3 space-y-2">
                      {/* Description */}
                      <p className="text-[11px] text-slate-500 italic leading-relaxed">
                        {phase.description}
                      </p>

                      {/* 3D Snapshot viewer */}
                      {hasSnapshots && (
                        <SnapshotViewer
                          snapshots={phase.snapshots!}
                          palletLengthCm={palletLengthCm}
                          palletWidthCm={palletWidthCm}
                          palletBaseHeightCm={palletBaseHeightCm}
                        />
                      )}

                      {/* Details */}
                      {phase.details && (
                        <details className="group">
                          <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-700 font-medium">
                            Datos detallados
                          </summary>
                          <div className="mt-2 bg-slate-50 rounded-lg border border-slate-200 p-3 max-h-[400px] overflow-y-auto custom-scrollbar">
                            <DetailsRenderer data={phase.details} />
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
